import type { NavigateFunction } from 'react-router-dom';
import axios from 'axios';
import { serviceUrls } from '../config/serviceUrls';
import { ROUTES, TOKEN_STORAGE_KEYS } from './constants';
import { getAccessToken, getRefreshToken, setTokens } from './token';
import { notifyAccessTokenStateChanged } from './accessTokenState';
import { getLoginPathForCurrentSession, preservePortalForLogout } from './loginPortal';

/** Thrown after auth-expired handling so callers skip generic error UI / raw JSON. */
export class AuthSessionExpiredHandledError extends Error {
  constructor() {
    super('AuthSessionExpiredHandled');
    this.name = 'AuthSessionExpiredHandledError';
    Object.setPrototypeOf(this, AuthSessionExpiredHandledError.prototype);
  }
}

export function isAuthSessionExpiredHandledError(e: unknown): boolean {
  return e instanceof AuthSessionExpiredHandledError;
}

const AUTH_FAILURE_CODES = new Set([
  'AUTH_INVALID_TOKEN',
  'AUTH_MISSING_TOKEN',
  'UNAUTHORIZED',
  'TOKEN_EXPIRED',
]);

/** Service/gateway internal auth — not the user's session; do not force logout. */
const NON_SESSION_AUTH_CODES = new Set([
  'AUTH_MISSING_GATEWAY_TOKEN',
  'AUTH_INVALID_GATEWAY_TOKEN',
]);

let logoutRedirectStarted = false;
/** Bumped on logout so in-flight token refresh cannot restore a cleared session. */
let authSessionEpoch = 0;

/** Single-flight refresh shared by axios interceptors and protectedFetch. */
let refreshInFlight: Promise<string | null> | null = null;

type RefreshTokenResponse = {
  access_token: string;
  refresh_token: string;
  token_type: string;
};

export function isLogoutRedirectInProgress(): boolean {
  return logoutRedirectStarted;
}

/** Clears the axios interceptor logout lock (e.g. after manual logout or new login). */
export function resetLogoutRedirectState(): void {
  logoutRedirectStarted = false;
}

/** Blocks token refresh from re-establishing session until the next login. */
export function markAuthSessionLoggedOut(): void {
  logoutRedirectStarted = true;
}

export function bumpAuthSessionEpoch(): number {
  authSessionEpoch += 1;
  return authSessionEpoch;
}

export function getAuthSessionEpoch(): number {
  return authSessionEpoch;
}

export function isAuthSessionEpochCurrent(epoch: number): boolean {
  return epoch === authSessionEpoch;
}

/** @internal Vitest only — resets module logout guard between tests. */
export function __resetAuthSessionLogoutStateForTests(): void {
  resetLogoutRedirectState();
  authSessionEpoch = 0;
  refreshInFlight = null;
}

/**
 * Single-flight access-token refresh for axios + protectedFetch.
 * Returns the new access token, or null when refresh is unavailable / rejected.
 */
export async function refreshAccessTokenSingleFlight(): Promise<string | null> {
  if (isLogoutRedirectInProgress()) {
    return null;
  }
  if (refreshInFlight) {
    return refreshInFlight;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  const epochAtStart = getAuthSessionEpoch();
  refreshInFlight = (async (): Promise<string | null> => {
    try {
      const refreshClient = axios.create({
        baseURL: serviceUrls.gateway,
        headers: { 'Content-Type': 'application/json' },
      });
      const { data } = await refreshClient.post<RefreshTokenResponse>('/auth/refresh', {
        refresh_token: refreshToken,
      });

      if (
        !isAuthSessionEpochCurrent(epochAtStart) ||
        isLogoutRedirectInProgress() ||
        !getRefreshToken()
      ) {
        return null;
      }

      setTokens(data.access_token, data.refresh_token);
      return data.access_token;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Clears auth tokens and identity headers.
 * Keeps `tkm_last_portal` and `tkm_selected_role` so logout/expiry can restore
 * the correct login shell after storage is wiped.
 */
export function clearAuthSessionStorage(): void {
  try {
    localStorage.removeItem('X_ROLE');
    localStorage.removeItem('X_TENANT_ID');
    localStorage.removeItem('X_USER_ID');
    localStorage.removeItem('tamil_editor_content');
    localStorage.removeItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN);
    // Keep tkm_last_portal + tkm_selected_role for portal-aware login redirects.
  } catch {
    // ignore localStorage errors
  }
  notifyAccessTokenStateChanged();
}

function normalizeBodyText(body: unknown): string {
  if (body == null) return '';
  if (typeof body === 'string') return body;
  try {
    return JSON.stringify(body);
  } catch {
    return String(body);
  }
}

function readErrorFieldsFromBody(body: unknown): { code?: string; message?: string } {
  const text = normalizeBodyText(body).trim();
  if (!text) return {};

  let obj: unknown = body;
  if (typeof body === 'string') {
    try {
      obj = JSON.parse(body) as unknown;
    } catch {
      return { message: body };
    }
  }

  if (!obj || typeof obj !== 'object') return { message: text };

  const o = obj as Record<string, unknown>;
  const directCode = typeof o.error_code === 'string' ? o.error_code : undefined;
  const directMessage =
    typeof o.message === 'string'
      ? o.message
      : typeof o.detail === 'string'
        ? o.detail
        : undefined;

  const err = o.error;
  if (err && typeof err === 'object') {
    const e = err as Record<string, unknown>;
    const nestedCode =
      typeof e.error_code === 'string'
        ? e.error_code
        : typeof e.code === 'string'
          ? e.code
          : undefined;
    const nestedMessage =
      typeof e.message === 'string'
        ? e.message
        : typeof e.detail === 'string'
          ? e.detail
          : undefined;
    return {
      code: nestedCode ?? directCode ?? (typeof o.code === 'string' ? o.code : undefined),
      message: nestedMessage ?? directMessage,
    };
  }

  return {
    code: directCode ?? (typeof o.code === 'string' ? o.code : undefined),
    message: directMessage,
  };
}

function messageIndicatesAuthFailure(message: string | undefined): boolean {
  if (!message) return false;
  const m = message.toLowerCase();
  return (
    m.includes('missing bearer token') ||
    m.includes('invalid token') ||
    m.includes('invalid bearer token') ||
    m.includes('expired access token') ||
    m.includes('invalid/expired access token')
  );
}

/**
 * True when an API response indicates invalid/expired/missing auth.
 */
export function isUnauthorizedApiResponse(status: number, body?: unknown): boolean {
  const { code, message } = readErrorFieldsFromBody(body);
  if (code && NON_SESSION_AUTH_CODES.has(code)) {
    return false;
  }
  if (status === 401) return true;

  if (code && AUTH_FAILURE_CODES.has(code)) {
    return status >= 400 && status < 500;
  }
  if (messageIndicatesAuthFailure(message)) {
    return status >= 400 && status < 500;
  }

  const text = normalizeBodyText(body).toLowerCase();
  if (text.includes('auth_invalid_token') || text.includes('missing bearer token')) {
    return status >= 400 && status < 500;
  }

  return false;
}

/**
 * Clears session storage and redirects to the correct portal login
 * (unless already on login). Safe to call multiple times; only one redirect is started.
 *
 * Order: resolve portal → preserve lastPortal → clear auth → navigate.
 */
export function forceLogoutAndRedirect(navigate?: NavigateFunction): void {
  if (typeof window === 'undefined') {
    clearAuthSessionStorage();
    return;
  }

  const path = window.location.pathname;
  if (path === ROUTES.LOGIN || path.startsWith(`${ROUTES.LOGIN}/`)) {
    clearAuthSessionStorage();
    return;
  }

  if (logoutRedirectStarted) return;

  const loginPath = getLoginPathForCurrentSession({ pathname: path });
  preservePortalForLogout({ pathname: path });
  clearAuthSessionStorage();

  logoutRedirectStarted = true;

  if (navigate) {
    navigate(loginPath, { replace: true });
    return;
  }

  window.location.replace(loginPath);
}

/**
 * For raw `fetch` callers: if unauthorized, logout + redirect and return true.
 */
export function handleUnauthorizedResponseIfPresent(options: {
  status: number;
  bodyText?: string;
  body?: unknown;
  navigate?: NavigateFunction;
}): boolean {
  if (logoutRedirectStarted) return true;

  const body =
    options.body ??
    (options.bodyText != null && options.bodyText !== '' ? options.bodyText : undefined);

  if (!isUnauthorizedApiResponse(options.status, body)) return false;

  forceLogoutAndRedirect(options.navigate);
  return true;
}

/**
 * For axios interceptors: logout + redirect when unauthorized; rejects with handled error.
 */
export function rejectWithAuthSessionExpired<T = never>(): Promise<T> {
  if (!logoutRedirectStarted) {
    forceLogoutAndRedirect();
  }
  return Promise.reject(new AuthSessionExpiredHandledError());
}

function newBrowserTraceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `fe-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

function authHeaderRecord(): Record<string, string> {
  const token = getAccessToken();
  const role = localStorage.getItem('X_ROLE');
  const tenantId = localStorage.getItem('X_TENANT_ID');
  const userId = localStorage.getItem('X_USER_ID');

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'x-trace-id': newBrowserTraceId(),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (role) {
    headers['x-role'] = role;
  }
  if (tenantId) {
    headers['x-tenant-id'] = tenantId;
  }
  if (userId) {
    headers['x-user-id'] = userId;
  }

  return headers;
}

function mergeHeadersInit(base: Record<string, string>, extra?: HeadersInit): HeadersInit {
  if (!extra) {
    return base;
  }

  if (extra instanceof Headers) {
    const merged = new Headers(base);
    extra.forEach((value, key) => merged.set(key, value));
    return merged;
  }

  if (Array.isArray(extra)) {
    const merged = new Headers(base);
    for (const [key, value] of extra) {
      if (value != null) {
        merged.set(key, value);
      }
    }
    return merged;
  }

  return { ...base, ...extra };
}

/** Standard auth + tenant headers for raw `fetch()` (axios interceptors do not apply). */
export function buildAuthFetchHeaders(extra?: HeadersInit): HeadersInit {
  return mergeHeadersInit(authHeaderRecord(), extra);
}

/**
 * `fetch()` wrapper for protected APIs: attaches bearer + tenant headers.
 * On 401 / auth error codes, attempts a single shared token refresh + retry,
 * then redirects to login only if refresh fails (same contract as axios).
 */
export async function protectedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  options?: { navigate?: NavigateFunction; _retry?: boolean }
): Promise<Response> {
  const urlStr =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url;

  const response = await fetch(input, {
    ...init,
    headers: mergeHeadersInit(authHeaderRecord(), init?.headers),
  });

  if (response.ok) {
    return response;
  }

  const bodyText = await response.clone().text().catch(() => '');

  if (
    isUnauthorizedApiResponse(response.status, bodyText) &&
    !options?._retry &&
    !urlStr.includes('/auth/refresh') &&
    !isLogoutRedirectInProgress()
  ) {
    const accessToken = await refreshAccessTokenSingleFlight();
    if (accessToken) {
      return protectedFetch(input, init, { ...options, _retry: true });
    }
  }

  if (
    handleUnauthorizedResponseIfPresent({
      status: response.status,
      bodyText,
      navigate: options?.navigate,
    })
  ) {
    throw new AuthSessionExpiredHandledError();
  }

  return response;
}
